import React, { useState, useRef, useEffect } from 'react';
import {
  Upload, X, FileText, Image as ImageIcon, Eye, Trash2, Filter,
} from 'lucide-react';
import {
  Attachment, AttachmentEntityType, AttachmentFileCategory, UploadAttachmentDto,
  FILE_CATEGORY_LABELS, ALLOWED_FILE_TYPES, MAX_FILE_SIZE_BYTES,
} from '../../types/attachment';
import { attachmentService } from '../../services/attachment/attachmentService';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Modal } from '../ui/Modal';

interface AttachmentManagerProps {
  entityType: AttachmentEntityType;
  entityId: string;
  readonly?: boolean;
}

const CATEGORY_OPTIONS: { value: AttachmentFileCategory; label: string }[] = [
  { value: 'photo_before', label: 'Before Photo' },
  { value: 'photo_during', label: 'During Photo' },
  { value: 'photo_after', label: 'After Photo' },
  { value: 'document', label: 'Document' },
  { value: 'quotation', label: 'Quotation' },
  { value: 'report', label: 'Report' },
  { value: 'invoice', label: 'Invoice' },
  { value: 'other', label: 'Other' },
];

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function getFileIcon(type: string): React.ReactNode {
  if (type.startsWith('image/')) return <ImageIcon className="w-5 h-5 text-sky-400" />;
  if (type === 'application/pdf') return <FileText className="w-5 h-5 text-rose-400" />;
  return <FileText className="w-5 h-5 text-slate-400" />;
}

export const AttachmentManager: React.FC<AttachmentManagerProps> = ({ entityType, entityId, readonly }) => {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [filterCategory, setFilterCategory] = useState<AttachmentFileCategory | 'all'>('all');
  const [selectedCategory, setSelectedCategory] = useState<AttachmentFileCategory>('photo_before');
  const [previewAtt, setPreviewAtt] = useState<Attachment | null>(null);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchAttachments = async () => {
    setIsLoading(true);
    const res = await attachmentService.getByEntity({ entity_type: entityType, entity_id: entityId });
    if (res.success) setAttachments(res.data);
    setIsLoading(false);
  };

  useEffect(() => { fetchAttachments(); }, [entityType, entityId]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setError('');

    for (const file of Array.from(files)) {
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        setError(`File type not allowed: ${file.name}`);
        continue;
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        setError(`File too large: ${file.name} (max 25MB)`);
        continue;
      }

      setIsUploading(true);
      const dto: UploadAttachmentDto = {
        entity_type: entityType,
        entity_id: entityId,
        file,
        file_category: selectedCategory,
      };
      const res = await attachmentService.upload(dto);
      if (res.success) {
        setAttachments(prev => [...prev, res.data]);
      } else {
        setError(res.message || 'Upload failed');
      }
      setIsUploading(false);
    }
    e.target.value = '';
  };

  const handleDelete = async (id: string) => {
    const res = await attachmentService.delete(id);
    if (res.success) {
      setAttachments(prev => prev.filter(a => a.id !== id));
    }
  };

  const filtered = filterCategory === 'all'
    ? attachments
    : attachments.filter(a => a.file_category === filterCategory);

  const grouped = CATEGORY_OPTIONS.reduce<Record<string, Attachment[]>>((acc, opt) => {
    const items = attachments.filter(a => a.file_category === opt.value);
    if (items.length > 0) acc[opt.value] = items;
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="w-3.5 h-3.5 text-slate-500" />
          <button
            onClick={() => setFilterCategory('all')}
            className={`px-2.5 py-1 rounded-lg text-[10px] font-medium transition-colors ${
              filterCategory === 'all' ? 'bg-sky-600 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            All ({attachments.length})
          </button>
          {CATEGORY_OPTIONS.filter(opt => grouped[opt.value]?.length > 0).map(opt => (
            <button
              key={opt.value}
              onClick={() => setFilterCategory(opt.value)}
              className={`px-2.5 py-1 rounded-lg text-[10px] font-medium transition-colors ${
                filterCategory === opt.value ? 'bg-sky-600 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
              }`}
            >
              {opt.label} ({grouped[opt.value].length})
            </button>
          ))}
        </div>

        {!readonly && (
          <div className="flex items-center gap-2">
            <select
              value={selectedCategory}
              onChange={e => setSelectedCategory(e.target.value as AttachmentFileCategory)}
              className="bg-slate-800 border border-slate-700 rounded-lg px-2 py-1 text-[10px] text-slate-200 focus:ring-2 focus:ring-sky-500"
            >
              {CATEGORY_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <Button
              size="sm"
              variant="outline"
              icon={<Upload className="w-3.5 h-3.5" />}
              onClick={() => fileInputRef.current?.click()}
              isLoading={isUploading}
            >
              Upload
            </Button>
            <input ref={fileInputRef} type="file" multiple accept={ALLOWED_FILE_TYPES.join(',')} className="hidden" onChange={handleUpload} />
          </div>
        )}
      </div>

      {error && (
        <div className="p-2 bg-rose-500/10 border border-rose-500/20 text-rose-400 rounded-lg text-xs">
          {error}
          <button onClick={() => setError('')} className="ml-2 underline">dismiss</button>
        </div>
      )}

      {isLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="bg-slate-800 border border-slate-700 rounded-lg h-32 animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-8 bg-slate-800/50 border border-slate-700/60 rounded-xl">
          <Upload className="w-8 h-8 text-slate-600 mx-auto mb-2" />
          <p className="text-xs text-slate-400">No attachments uploaded yet</p>
          {!readonly && <p className="text-[10px] text-slate-500 mt-1">Click Upload to add files</p>}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {filtered.map(att => (
            <div key={att.id} className="group relative bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
              {att.thumbnail_url ? (
                <img src={att.thumbnail_url} alt={att.file_name} className="w-full h-24 object-cover" />
              ) : (
                <div className="w-full h-24 flex items-center justify-center bg-slate-900">
                  {getFileIcon(att.file_type)}
                </div>
              )}
              <div className="p-2 space-y-1">
                <p className="text-[10px] text-slate-300 truncate font-medium">{att.file_name}</p>
                <div className="flex items-center justify-between">
                  <Badge label={FILE_CATEGORY_LABELS[att.file_category]} variant="neutral" className="text-[8px] px-1 py-0" />
                  <span className="text-[8px] text-slate-500">{formatFileSize(att.file_size)}</span>
                </div>
              </div>
              <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => setPreviewAtt(att)}
                  className="p-1 bg-sky-600 rounded-full"
                >
                  <Eye className="w-3 h-3 text-white" />
                </button>
                {!readonly && (
                  <button
                    onClick={() => handleDelete(att.id)}
                    className="p-1 bg-rose-600 rounded-full"
                  >
                    <Trash2 className="w-3 h-3 text-white" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {previewAtt && (
        <Modal isOpen={!!previewAtt} onClose={() => setPreviewAtt(null)} title={previewAtt.file_name}>
          <div className="space-y-3">
            {previewAtt.preview_url ? (
              previewAtt.file_type.startsWith('image/') ? (
                <img src={previewAtt.preview_url} alt={previewAtt.file_name} className="w-full rounded-lg" />
              ) : (
                <iframe src={previewAtt.preview_url} className="w-full h-96 rounded-lg" title={previewAtt.file_name} />
              )
            ) : (
              <div className="h-64 flex items-center justify-center bg-slate-800 rounded-lg">
                <FileText className="w-12 h-12 text-slate-600" />
              </div>
            )}
            <div className="flex items-center justify-between text-xs text-slate-400">
              <span>{formatFileSize(previewAtt.file_size)} · {previewAtt.file_type}</span>
              <span>Uploaded by {previewAtt.uploaded_by_user_name}</span>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
